import type { CustomErrorParams, ZodNumber, ZodTypeAny } from "zod";
import { z as zod } from "zod";
import { makeZodI18nMap } from "zod-i18n-map";
import { isAfter } from "date-fns";
import type { ParseKeys } from "i18next";
import i18n from "i18next";

import { isPhoneValid } from "@/utils/phoneNumber";
import type { IFileObject } from "@/interfaces";

const dropdown = (optional = false, params?: CustomErrorParams) =>
  zod.custom<{ key: string; value: string }>(
    (val) => {
      return optional || (val && val.key && val.value);
    },
    {
      ...params,
      params: {
        ...params?.params,
        i18n: "errors.invalid_type_received_undefined",
      },
    },
    // custom errors are fetched from zod.json (at obj root)
  );

export const phone = (optional = false) =>
  zod.custom<{ code: string; number: string }>().superRefine((val, ctx) => {
    if (optional) {
      if (val?.number?.length > 0) {
        const isValid = isPhoneValid(val);

        if (!isValid) {
          ctx.addIssue({
            code: "custom",
            params: { i18n: "invalidPhoneNumber" },
          });
        }
      }
    } else if (val === null || val === undefined || !val.number || !val.code) {
      ctx.addIssue({
        code: "custom",
        params: { i18n: "errors.invalid_type_received_undefined" },
      });
    } else {
      const isValid = isPhoneValid(val);

      if (!isValid) {
        ctx.addIssue({
          code: "custom",
          params: { i18n: "invalidPhoneNumber" },
        });
      }
    }
  });

const website = (params?: CustomErrorParams) =>
  zod.custom<string | undefined | null>(
    (url) => {
      // Allow the field to be optional: accept undefined, null, or empty string
      if (
        url === undefined ||
        url === null ||
        (typeof url === "string" && url.trim() === "")
      )
        return true;

      // Ensure the input is a string before proceeding with further validation
      if (typeof url !== "string") return false;

      // Updated regex that requires http:// or https:// at the beginning
      const pattern =
        /^(https?:\/\/)([\w-]+(\.[\w-]+)+)([\w#%&+,./:=?@^~-]*[\w#%&+/=?@^~-])?$/;

      return pattern.test(url);
    },
    {
      ...params,
      params: { ...params?.params, i18n: "invalidWebsite" },
    },
  );

// birthDate: z
//   .string()
//   .transform((d) => new Date(d))
//   .pipe(z.date().max(date))

const transformedNumber = (target: ZodNumber, optional: boolean) =>
  optional
    ? z
        .string()
        .optional()
        .transform((char) => {
          if (char === "" || char === undefined) return 0;
          return Number(char);
        })
        .pipe(target)
    : z.string().transform(Number).pipe(target);

export const minAge = (age = 18) =>
  zod.string().superRefine((val, ctx) => {
    const date = new Date();

    date.setFullYear(date.getFullYear() - age);
    const receivedDate = new Date(val);

    if (isAfter(receivedDate, date)) {
      ctx.addIssue({
        code: "custom",
        params: { i18n: "minAge" },
      });
    }
  });

const nationalId = (params?: CustomErrorParams) =>
  zod.custom<string>(
    (id) => {
      if (import.meta.env.VITE_LANGUAGE !== "fa") {
        return true;
      }
      // First, ensure 'id' is a string
      if (typeof id !== "string") return false;

      // Iranian National ID should be exactly 10 digits
      if (!/^\d{10}$/.test(id)) return false;

      let sum = 0;

      for (let i = 0; i < 9; i++) {
        sum += Number.parseInt(id.charAt(i)) * (10 - i);
      }
      const remainder = sum % 11;

      return (
        (remainder < 2 && Number.parseInt(id.charAt(9)) === remainder) ||
        (remainder >= 2 && Number.parseInt(id.charAt(9)) === 11 - remainder)
      );
    },
    {
      ...params,
      params: { ...params?.params, i18n: "invalidNationalId" },
    },
  );

const email = (params?: CustomErrorParams) =>
  zod.custom<string | undefined | null>(
    (email) => {
      // Ensure email is either undefined, null, or a non-empty string
      if (
        email === undefined ||
        email === null ||
        (typeof email === "string" && email.trim() === "")
      )
        return true;

      // If email is a string, perform regex validation
      if (typeof email === "string") {
        const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        return pattern.test(email);
      }

      // If email is not a string, undefined, or null, it's invalid
      return false;
    },
    {
      ...params,
      params: { ...params?.params, i18n: "invalidEmail" },
    },
  );

const postalCode = (params?: CustomErrorParams) =>
  zod.custom<string | undefined | null>(
    (code) => {
      if (import.meta.env.VITE_LANGUAGE !== "fa") return true;
      // Allow the field to be optional: accept undefined, null, or empty string
      if (
        code === undefined ||
        code === null ||
        (typeof code === "string" && code.trim() === "")
      )
        return true;

      // Ensure the input is a string before proceeding with further validation
      if (typeof code !== "string") return false;

      // Regex for Iranian postal code: exactly 10 digits
      const pattern = /^\d{10}$/;

      return pattern.test(code);
    },
    {
      ...params,
      params: { ...params?.params, i18n: "invalidPostalCode" },
    },
  );

const fileObject = (optional = false) =>
  zod.custom<IFileObject>().refine(
    (val) => {
      return (
        optional ||
        (val?.id !== undefined &&
          val?.id !== null &&
          val?.id?.toString()?.trim() !== "")
      );
    },
    { params: { i18n: "file" } },
  );

const depositAmount = (maxValue = 10_000_000) =>
  zod
    .string()
    .regex(/^\d+(\.\d+)?$/, {
      message: i18n.t("deposit-withdraw-error.amount", "common"),
    })
    .refine(
      (value) =>
        !Number.isNaN(Number.parseFloat(value)) &&
        Number.parseFloat(value) <= maxValue,
      {
        message: i18n.t("deposit-withdraw-error.maxAmount", "common"),
      },
    )
    .refine(
      (value) =>
        !Number.isNaN(Number.parseFloat(value)) && Number.parseFloat(value) > 0,
      {
        message: i18n.t("deposit-withdraw-error.amount", "common"),
      },
    );

zod.setErrorMap(makeZodI18nMap({ ns: ["zod", "form"] }));

export type IShapeOf<T> = Partial<Record<keyof T, ZodTypeAny>>;

export type IShapeOfForm<T> = Partial<
  Record<keyof T & ParseKeys<"form">, ZodTypeAny>
>;

// eslint-disable-next-line id-length
export const z = {
  ...zod,
  dropdown,
  phone,
  minAge,
  website,
  nationalId,
  email,
  postalCode,
  transformedNumber,
  depositAmount,
  fileObject,
};
